import { useState, useRef, useEffect } from "react";
import { eng } from "@/lib/engineApi";
import { useGrid, allowed, type ViewId } from "@/state/grid";
import { signOut } from "@/lib/auth";
import { subscribeQueue } from "@/lib/offlineQueue";
import { exportHandoverPack } from "@/lib/handover";
import { LOGO_DATA_URI } from "@/assets/logo";
import ItpManagerModal from "./ItpManagerModal";
import CadImportModal from "./CadImportModal";
import ProjectSwitcher from "./ProjectSwitcher";
import NotificationBell from "./NotificationBell";
import AuditLogModal from "./AuditLogModal";

const TABS: [ViewId, string][] = [
  ["overview", "Overview"],
  ["rfi", "RFI Register"],
  ["sitemap", "Site Map"],
  ["qc", "Quality Control"],
  ["punch", "Punch List"],
  ["tdd", "Documents"],
];

/** Topbar + project ribbon. React port of the engine's chrome() shell. */
export default function Chrome() {
  const version = useGrid((s) => s.version);
  const view = useGrid((s) => s.view);
  const setView = useGrid((s) => s.setView);
  const bump = useGrid((s) => s.bump);
  void version;

  const e = eng();
  const D = e.D;
  const role = D.roles[e.currentRole as keyof typeof D.roles];
  const tabs = TABS.filter(([v]) => allowed(v, e.currentRole));
  const punchOpen = e.punchCounts().open;
  const rfiPending = e.rfiCounts().pending;

  // Dynamic field + progress chip for the single-row header (UI brief #1). Follows
  // the currently-selected field (Site Map selection, else the QC field) and shows
  // its acceptance %, derived from the engine's effective per-table status.
  const hFieldId = e.state.selField || e.state.qcField || D.fields[0]?.id;
  const hField = D.fields.find((x: any) => x.id === hFieldId) || D.fields[0];
  let hPass = 0;
  if (hField) for (let t = 1; t <= hField.tables; t++) if (e.tableEffectiveStatus(hField.id, t).status === "pass") hPass++;
  const hPct = hField && hField.tables ? Math.round((hPass / hField.tables) * 100) : 0;

  const [gearOpen, setGearOpen] = useState(false);
  const [modal, setModal] = useState<null | "cad" | "itp" | "audit">(null);
  const [queued, setQueued] = useState(0);
  const [online, setOnline] = useState(typeof navigator === "undefined" ? true : navigator.onLine);
  const fileRef = useRef<HTMLInputElement>(null);
  const isManager = e.currentRole === "qcmanager";

  useEffect(() => subscribeQueue(setQueued), []);
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);

  return (
    <>
      <div className="topbar">
        <div className="mark">
          <div className="glyph">
            <img src={LOGO_DATA_URI} alt="TerraTrace logo" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
          </div>
          <div className="nm">
            <b className="serif">GridProof</b>
            <span>powered by TerraTrace</span>
          </div>
        </div>
        <div className="vr" />
        <ProjectSwitcher />
        <div className="vr" />
        <div className="nav">{/* React owns nav; no #topnav so engine refreshBadges() no-ops */}
          {tabs.map(([v, label]) => {
            const pc = v === "punch" ? punchOpen : v === "rfi" ? rfiPending : 0;
            return (
              <button key={v} className={`tab ${view === v ? "active" : ""}`} onClick={() => setView(v)}>
                <span className="ti" />
                {label}
                {pc ? <span className="navbadge">{pc}</span> : null}
              </button>
            );
          })}
        </div>
        <div className="topright">
          {hField && (
            <button
              className="hfield"
              title={`${hField.id} · ${hPass}/${hField.tables} tables accepted — open Site Map`}
              onClick={() => setView("sitemap")}
            >
              <span className="hf-name">{hField.id}</span>
              <span className="hf-pct">{hPct}%</span>
            </button>
          )}
          <NotificationBell />
          <button
            className="themebtn"
            title="Toggle light / dark mode"
            onClick={() => {
              e.toggleTheme();
              bump();
            }}
          >
            {e.theme === "dark" ? "☾" : "☀"}
          </button>
          <button className="rolebtn" title="Active role" style={{ ["--rcol" as any]: role.color }}>
            <span className="rolebtn-dot" />
            <span className="rolebtn-label">
              <b>{role.operator}</b>
              <span className="rolebtn-role"> · {role.label}</span>
            </span>
          </button>
          <div className={`gear-wrap ${gearOpen ? "open" : ""}`}>
            <button className="iconbtn" title="Settings & export" onClick={() => setGearOpen((o) => !o)}>
              ⚙
            </button>
            {gearOpen && (
              <div className="gear-menu" style={{ display: "block" }}>
                <div className="gear-section">History</div>
                <button className="gear-item" onClick={() => { e.undo(); setGearOpen(false); }}>↶ Undo</button>
                <button className="gear-item" onClick={() => { e.redo(); setGearOpen(false); }}>↷ Redo</button>
                <div className="gear-divider" />
                <div className="gear-section">Data &amp; backup</div>
                <button className="gear-item" onClick={() => { e.exportSnapshot(); setGearOpen(false); }}>⬇ Save snapshot</button>
                <button className="gear-item" onClick={() => fileRef.current?.click()}>⬆ Load snapshot</button>
                <div className="gear-divider" />
                <div className="gear-section">Project topology</div>
                <button className="gear-item" onClick={() => { setModal("cad"); setGearOpen(false); }}>▦ Import from CAD/DWG…</button>
                <button className="gear-item" onClick={() => { setModal("itp"); setGearOpen(false); }}>☑ Manage inspection items…</button>
                <div className="gear-divider" />
                <div className="gear-section">Reports</div>
                <button className="gear-item" onClick={() => { e.exportExecutiveReport(); setGearOpen(false); }}>▣ Executive project report</button>
                <button
                  className="gear-item"
                  onClick={() => {
                    const fid = e.state.selField || e.state.qcField || e.D.fields[0]?.id;
                    if (fid) e.exportFieldReport(fid);
                    setGearOpen(false);
                  }}
                  title="Acceptance report for the currently selected field"
                >
                  ▤ Field report (selected field)
                </button>
                <button className="gear-item" onClick={() => { e.exportPunchPDF(); setGearOpen(false); }}>▥ Punch list report</button>
                <div className="gear-divider" />
                <div className="gear-section">Compliance &amp; handover</div>
                <button className="gear-item" onClick={() => { exportHandoverPack(); setGearOpen(false); }}>⧉ Export handover pack…</button>
                {isManager && (
                  <button className="gear-item" onClick={() => { setModal("audit"); setGearOpen(false); }}>☰ Audit trail…</button>
                )}
                <div className="gear-divider" />
                <div className="gear-section">Session</div>
                <button className="gear-item" onClick={() => signOut().then(() => location.reload())}>⎋ Sign out</button>
              </div>
            )}
            <input
              ref={fileRef}
              type="file"
              accept=".json"
              style={{ display: "none" }}
              onChange={(ev) => {
                const f = ev.target.files?.[0];
                if (f) e.importSnapshot(f);
                setGearOpen(false);
              }}
            />
          </div>
          <div
            className="gps"
            title={online ? (queued ? `${queued} change(s) waiting to sync` : "Online · all changes synced") : "Offline · changes are queued locally and will sync on reconnect"}
            style={!online ? { ["--dotcol" as any]: "#C0563B" } : queued ? { ["--dotcol" as any]: "#C9A227" } : undefined}
          >
            <span className="gdot" style={!online ? { background: "#C0563B" } : queued ? { background: "#C9A227" } : undefined} />
            <span className="glabel">
              {!online ? "Offline" : queued ? `Queued (${queued})` : "Online"}
            </span>
          </div>
        </div>
      </div>
      {modal === "cad" && <CadImportModal onClose={() => setModal(null)} />}
      {modal === "itp" && <ItpManagerModal onClose={() => setModal(null)} />}
      {modal === "audit" && <AuditLogModal onClose={() => setModal(null)} />}
    </>
  );
}
